import { Component, Injector, OnDestroy } from '@angular/core';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { assets } from '../../../core/constants/assets.const';
import { VerificationComponent } from '../verification/verification.component';
import { emailErrors } from '../../../core/constants/commonErrors.const';
import { Subject, takeUntil } from 'rxjs';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { countryCode } from '../../../models/countryCode.model';
import { CountryCodeConst } from '../../../core/constants/countryCode.const';
import { OtpType, SignUpType } from '../../../core/constants/keys.constant';
import { BaseClass } from '../../../shared/baseClass/baseClass';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'xplora-website-forgot-password',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    ReactiveFormsModule,
    MatTabsModule,
    NgxMatIntlTelInputComponent
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent extends BaseClass implements OnDestroy {
  protected forgotType = SignUpType.Email;
  protected selectedTab = 0;
  protected countryCodeObject: countryCode = CountryCodeConst;
  protected submitted = false;
  private destroy: Subject<boolean> = new Subject<boolean>();
  protected emailErrors = emailErrors;
  protected assetsConst = assets;
  protected forgotPasswordFormGroup: FormGroup;
  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ForgotPasswordComponent>,
    private _authService: AuthService
  ) {
    super(injector);
    this.forgotPasswordFormGroup = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      phoneNo: [
        { value: '',
          disabled: true }, [Validators.required]
      ],
      dialCode: ['']
    });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.submitted = false;
    this.selectedTab = event.index;
    if (this.selectedTab === 0) {
      this.forgotType = SignUpType.Email;
      this.forgotPasswordFormGroup.get('email')?.enable();
      this.forgotPasswordFormGroup.get('phoneNo')?.disable();
      this.forgotPasswordFormGroup.get('dialCode')?.disable();
      this.forgotPasswordFormGroup.get('email')?.updateValueAndValidity();
      this.forgotPasswordFormGroup.get('phoneNo')?.updateValueAndValidity();
      this.forgotPasswordFormGroup.get('dialCode')?.updateValueAndValidity();
    } else {
      this.forgotType = SignUpType.Phone;
      this.forgotPasswordFormGroup.get('email')?.disable();
      this.forgotPasswordFormGroup.get('phoneNo')?.enable();
      this.forgotPasswordFormGroup.get('dialCode')?.enable();
      this.forgotPasswordFormGroup.get('email')?.updateValueAndValidity();
      this.forgotPasswordFormGroup.get('phoneNo')?.updateValueAndValidity();
      this.forgotPasswordFormGroup.get('dialCode')?.updateValueAndValidity();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countryChangedEvent(event: any): void {
    this.countryCodeObject = event;
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.forgotPasswordFormGroup.valid) {
      const modifiedValue = this.patchValues();
      delete modifiedValue.dialCode;
      this.spinnerService.addToLoader('userLogin');
      this._authService
        .forgotPassword(modifiedValue)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: () => {
            this.spinnerService.removeFromLoader('userLogin');
            this.dialog.open(VerificationComponent, {
              panelClass: ['modal', 'auth_modal'],
              data: {
                resetPassword: true,
                signUpType:
                  this.selectedTab === 0 ? SignUpType.Email : SignUpType.Phone,
                otpType: OtpType.Forgot,
                data: modifiedValue
              }
            });
            this.dialogRef.close();
          },
          error: () => {
            this.spinnerService.removeFromLoader('userLogin');
          }
        });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patchValues(): any {
    // eslint-disable-next-line prefer-const, @typescript-eslint/no-explicit-any
    let value: any;
    const patchValueToCountryCode = (): void => {
      let tempCountryCode = this.forgotPasswordFormGroup.get('dialCode')?.value;
      if (tempCountryCode) {
        tempCountryCode = tempCountryCode.split('');
        tempCountryCode.shift();
        tempCountryCode = tempCountryCode.join('');
      }
      if (tempCountryCode !== this.countryCodeObject.dialCode) {
        this.forgotPasswordFormGroup.patchValue({
          dialCode: `+${this.countryCodeObject.dialCode}`
        });
      }
    };
    const patchValueToPhoneField = (): void => {
      let phone = this.forgotPasswordFormGroup.get('phoneNo')?.value;
      if (
        phone &&
        phone.includes(this.forgotPasswordFormGroup.get('dialCode')?.value)
      ) {
        phone = phone.split(
          this.forgotPasswordFormGroup.get('dialCode')?.value
        );
        if (value.phoneNo !== undefined) {
          value.phoneNo = phone[1].trim();
        }
      }
    };
    patchValueToCountryCode();
    value = this.forgotPasswordFormGroup['value'];
    patchValueToPhoneField();
    return value;
  }

  ngOnDestroy(): void {
    this.destroy.complete();
    this.destroy.unsubscribe();
  }
}
